import { useState, useRef, useEffect } from 'react';
import useAuth from '../../hooks/useAuth';
import useVoice from '../../hooks/useVoice';
import VoiceMicButton from './VoiceMicButton';
import styles from './ChatPane.module.css';

export default function ChatInput({ onSend, disabled }) {
  const { session } = useAuth();
  const [text, setText] = useState('');
  const textareaRef = useRef(null);
  const { voiceState, isVoiceConnected, isMuted, disconnectVoice, toggleMute } = useVoice();

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [text]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (text.trim() && !disabled) {
        onSend(text.trim());
        setText('');
      }
    }
  };

  // Voice mode — show status panel with stop button to exit
  if (isVoiceConnected) {
    return (
      <div className={styles.inputArea}>
        <div className={styles.inputRow}>
          <VoiceStatusPanel voiceState={voiceState} isMuted={isMuted} />
          <button
            className={`${styles.voiceMuteBtn}${isMuted ? ` ${styles.voiceMuteBtnActive}` : ''}`}
            onClick={toggleMute}
            title={isMuted ? 'Unmute microphone' : 'Mute microphone'}
          >
            {isMuted ? <MicOffIcon /> : <MicOnIcon />}
          </button>
          <button
            className={styles.voiceExitBtn}
            onClick={disconnectVoice}
            title="Exit voice mode"
          >
            <StopIcon />
          </button>
        </div>
      </div>
    );
  }

  // Text mode — normal textarea + mic button (voice disabled for anonymous)
  return (
    <div className={styles.inputArea}>
      <div className={styles.inputRow}>
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message EGO..."
          rows={1}
          disabled={disabled}
        />
        <VoiceMicButton disabled={disabled || !session} />
      </div>
    </div>
  );
}

function VoiceStatusPanel({ voiceState, isMuted }) {
  if (isMuted) {
    return (
      <div className={styles.voicePanel}>
        <div className={styles.voiceStatus}>
          <span className={styles.voiceDotMuted} />
          <span className={styles.voiceLabel}>Muted</span>
        </div>
      </div>
    );
  }

  const config = {
    wait: { label: 'Listening...', type: 'listening' },
    query: { label: 'Listening...', type: 'listening' },
    processing: { label: 'Thinking...', type: 'thinking' },
    playback: { label: 'Speaking...', type: 'speaking' },
  };

  const state = config[voiceState] || config.wait;

  return (
    <div className={styles.voicePanel}>
      <div className={styles.voiceStatus}>
        {state.type === 'listening' && <span className={styles.voiceDot} />}
        {state.type === 'thinking' && <span className={styles.voiceSpinner} />}
        {state.type === 'speaking' && (
          <span className={styles.voiceSpeaker}><SpeakerIcon /></span>
        )}
        <span className={styles.voiceLabel}>{state.label}</span>
      </div>
    </div>
  );
}

function StopIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <rect x="4" y="4" width="16" height="16" rx="2" />
    </svg>
  );
}

function MicOnIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="1" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function MicOffIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12" />
      <line x1="12" y1="19" x2="12" y2="23" />
    </svg>
  );
}

function SpeakerIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
  );
}
